import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { db } from "@/lib/db-client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { audit } from "@/lib/audit-client";

export const Route = createFileRoute("/_authenticated/admin/pricing")({
  component: PricingAdmin,
});

function PricingAdmin() {
  const [clients, setClients] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [pricing, setPricing] = useState<any[]>([]);
  const [clientId, setClientId] = useState("");

  const load = async () => {
    const [c, m, p] = await Promise.all([
      db.from("clients").select("*").order("name"),
      db.from("materials").select("*").eq("active", true).order("name"),
      db.from("client_pricing").select("*"),
    ]);
    if (c.data) setClients(c.data);
    if (m.data) setMaterials(m.data);
    if (p.data) setPricing(p.data);
  };
  useEffect(() => { load(); }, []);

  const map = useMemo(() => {
    const o: Record<string, number> = {};
    pricing.forEach(r => { o[`${r.client_id}|${r.material_id}|${r.type}`] = Number(r.price_per_ton); });
    return o;
  }, [pricing]);

  const setPrice = async (materialId: string, type: "depot" | "reprise", value: string) => {
    if (!clientId) return;
    const price = parseFloat(value) || 0;
    const { error } = await db
      .from("client_pricing")
      .upsert(
        { client_id: clientId, material_id: materialId, type, price_per_ton: price },
        { onConflict: "client_id,material_id,type" }
      );
    if (error) return toast.error(error.message);
    await audit("pricing.update", {
      entity: "client_pricing",
      metadata: { client_id: clientId, material_id: materialId, type, price_per_ton: price },
    });
    load();
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Tarifs par client</h1>
      <Card>
        <CardHeader><CardTitle className="text-base">Sélectionner un client</CardTitle></CardHeader>
        <CardContent>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="max-w-sm"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
            <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </CardContent>
      </Card>

      {clientId && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Matière</TableHead>
                  <TableHead>Prix Dépôt (€/t)</TableHead>
                  <TableHead>Prix Reprise (€/t)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.map(m => (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell>
                      <Input
                        type="number" step="0.01" min="0"
                        defaultValue={map[`${clientId}|${m.id}|depot`] ?? ""}
                        onBlur={(e) => setPrice(m.id, "depot", e.target.value)}
                        className="max-w-[140px]"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number" step="0.01" min="0"
                        defaultValue={map[`${clientId}|${m.id}|reprise`] ?? ""}
                        onBlur={(e) => setPrice(m.id, "reprise", e.target.value)}
                        className="max-w-[140px]"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
